import appConfig from "config.json";
import { deleteCacheByUrlMatch, getCache, setCache } from "utils/cache";
import { getConversationIdCookie, getCookie, waitForCookie } from "utils/cookie";
import { filterContentByPredicate } from "utils/filterContent";
import {
	getLocalStorageValue,
	removeLocalStorageValue,
	setLocalStorageValue,
} from "utils/localStorage";
import { getEnvironment } from "utils";

const { discoveryAgentCheckUrl, discoveryAgentLoadBaseUrl } = appConfig;

// Some environments (e.g. load/lt01) point the check request at a dedicated
// Discovery Agent web API host instead of the current origin. When the active
// environment sets `discoveryAgent.useLoadUrl`, the check path is prefixed with
// `discoveryAgentLoadBaseUrl`; otherwise the relative path (current origin) is
// used, keeping the existing behavior for all other environments.
const resolveDaCheckUrl = () => {
	const environment = getEnvironment();
	const useLoadUrl =
		appConfig.environmentConfiguration?.[environment]?.discoveryAgent
			?.useLoadUrl;
	if (useLoadUrl && discoveryAgentLoadBaseUrl) {
		return `${discoveryAgentLoadBaseUrl}${discoveryAgentCheckUrl}`;
	}
	return discoveryAgentCheckUrl;
};

// localStorage key used to persist the Discovery Agent production toggle so it
// survives SPA navigation without re-passing the query param.
export const daToggleKey = "enableDA";

// Adobe Experience Cloud ID cookie, used as the DA device identifier. It is set
// asynchronously by Adobe, so the check waits for it before firing the request.
export const daDeviceIdCookie = "s_ecid";

// Accepted toggle values that map to the customDaLogic behavior.
const daToggleToLogic = {
	show: "show",
	check: "check",
	true: "check",
	false: "hide",
	hide: "hide",
};

/**
 * Whether Discovery Agent is enabled for the given brand. Mirrors the gate
 * used to filter DA content in NavigationTxp (brandConfig.discoveryAgentEnabled),
 * so DA-only side effects are skipped for brands without DA (e.g. DVC, DLR).
 */
export const isDiscoveryAgentEnabledForBrand = (brand) =>
	!!appConfig.brands?.[brand]?.discoveryAgentEnabled;

/**
 * Analytics `store` value for Discovery Agent entry-point links.
 * This is a literal template token intentionally sent as-is — the analytics
 * platform substitutes the Guest's line-of-business store at runtime.
 */
export const daAnalyticsStore = "{{store}}";

/**
 * Determines if a navigation item is a Discovery Agent link.
 * Matches by href containing "/discovery-agent/" or analyticsLinkId ending with "_DA".
 */
export const isDiscoveryAgentLink = (item) =>
	item?.href?.includes("/discovery-agent/") ||
	item?.link?.href?.includes("/discovery-agent/") ||
	item?.analyticsLinkId?.endsWith("_DA") ||
	item?.link?.analyticsLinkId?.endsWith("_DA");

/**
 * Filters Discovery Agent links from navigation sections.
 * When showDaLinks is true, returns sections unchanged.
 * When false, recursively removes any item matching DA link criteria.
 */
export const filterDiscoveryAgentLinks = (sections, showDaLinks) => {
	if (showDaLinks) return sections;
	return filterContentByPredicate(sections, isDiscoveryAgentLink);
};

/**
 * Resolves the effective Discovery Agent logic ("show" | "check" | "hide"),
 * applying a production toggle on top of the content-driven `customDaLogic`.
 *
 * The toggle is read straight from the URL query string (same pattern as
 * `dataNavTheme`) so anyone can activate DA just by passing `?enableDA=`, with
 * no dependency on the content/config propagating it. When a recognized value
 * is present it is persisted to localStorage so it survives SPA navigation
 * without re-passing the param; an explicit "false"/"hide" clears the override.
 *
 * Precedence: URL query param (`?enableDA=`) > localStorage > content
 * `customDaLogic`.
 *
 * @param {string} [customDaLogic] The content-driven value from chrome data.
 * @returns {string|undefined} "show", "check", "hide", or the original value.
 */
export const resolveDaLogic = (customDaLogic) => {
	const param = new URLSearchParams(window?.location?.search)
		.get(daToggleKey)
		?.toLowerCase();
	if (param) {
		const mapped = daToggleToLogic[param];
		if (mapped === "hide") {
			removeLocalStorageValue(daToggleKey);
			return "hide";
		}
		if (mapped) {
			setLocalStorageValue(daToggleKey, mapped);
			return mapped;
		}
	}
	const stored = getLocalStorageValue(daToggleKey);
	if (stored && daToggleToLogic[stored]) {
		return daToggleToLogic[stored];
	}
	return customDaLogic;
};

/**
 * Builds the colon-delimited conversationId analytics value for Discovery Agent
 * entry-point links: `advisorId:conversationId:bookableObjectIds`.
 *
 * The three parts are resolved from whatever application state is available at
 * click time. On the global header the Guest has not yet entered Discovery Agent,
 * so `advisorId` and `bookableObjectIds` are typically empty and `conversationId`
 * comes from a prior-session cookie when present.
 *
 * Returns `undefined` when every part is empty, so callers can omit the property
 * entirely and let the analytics platform drop it from the beacon (per spec).
 *
 * @param {Object} [parts] Optional overrides for the individual segments.
 * @param {string} [parts.advisorId] Discovery Agent advisor identifier.
 * @param {string} [parts.conversationId] Discovery Agent conversation identifier.
 * @param {string} [parts.bookableObjectIds] Comma-joined bookable object identifiers.
 * @returns {string|undefined} The colon-delimited value, or undefined when empty.
 */
export const buildDaConversationId = ({
	advisorId = "",
	conversationId = getConversationIdCookie() || "",
	bookableObjectIds = "",
} = {}) => {
	if (!advisorId && !conversationId && !bookableObjectIds) return undefined;
	return `${advisorId}:${conversationId}:${bookableObjectIds}`;
};

/**
 * Evaluates the Discovery Agent beta-program check API response.
 * Returns true (show links) when discoveryAgentsMainEnabled is true
 * AND either allowed or enrolled is true.
 */
export const evaluateDaResponse = (data) => {
	if (!data || data.discoveryAgentsMainEnabled !== true) return false;
	return data.allowed === true || data.enrolled === true;
};

// Cache the beta-program check for 15 minutes.
const daCheckCacheMinutes = 15;

/**
 * Builds the cache key for the Discovery Agent check.
 *
 * This key is used ONLY for the browser Cache API — it is NOT the request URL.
 * The Cache API keys entries by URL, so embedding the payload (swid + deviceId +
 * loggedIn) in a synthetic key makes the cache user-specific: a different user,
 * device or auth state produces a different key and forces a fresh request, while
 * the same user reuses the cached response until it expires. The actual request is
 * sent to the clean endpoint with no query parameters.
 */
export const buildDaCheckCacheKey = (swid, deviceId, loggedIn) => {
	const url = new URL(resolveDaCheckUrl(), window.location.origin);
	url.searchParams.set("swid", swid || "");
	url.searchParams.set("deviceId", deviceId || "");
	url.searchParams.set("loggedIn", String(!!loggedIn));
	return url.href;
};

/**
 * Removes every cached Discovery Agent check entry (all per-user variants).
 * Call this on login and logout so the beta-program access is re-evaluated
 * from scratch for the new session instead of reusing a stale cached result.
 *
 * No-op when Discovery Agent is not enabled for the given brand, mirroring the
 * DA content-filtering gate so brands without DA skip this side effect.
 *
 * @param {string} [brand] The current brand (callers already have it, e.g. profile).
 */
export const clearDaCheckCache = async (brand) => {
	if (!discoveryAgentCheckUrl || !isDiscoveryAgentEnabledForBrand(brand)) return;
	await deleteCacheByUrlMatch(resolveDaCheckUrl());
};

/**
 * Checks the Discovery Agent beta-program API to determine if DA links should be shown.
 * Requires user profile data to resolve SWID and auth state.
 *
 * The result is cached for 15 minutes, keyed by the swid/deviceId/loggedIn payload,
 * so the request fires at most once per 15 minutes per user and re-runs automatically
 * when the user, device or login state changes. The request itself is sent to the
 * clean endpoint (no query parameters); the payload rides in the POST body and, when
 * the Guest is logged in, the auth token is sent in the Authorization header.
 *
 * The request is deferred until the Adobe device id cookie (`s_ecid`) is present,
 * since it is set asynchronously; if it never appears the check resolves to false
 * without firing a request.
 *
 * Returns a promise that resolves to true (show) or false (hide).
 */
export const checkDiscoveryAgentAccess = async (user) => {
	if (!discoveryAgentCheckUrl) return false;
	const swid = user?.swid || getCookie("SWID");
	// Adobe sets s_ecid asynchronously; wait for it so the check never fires
	// with an empty deviceId (which would also poison the cache key). If it
	// never appears within the timeout, bail out without requesting.
	const deviceId = await waitForCookie(daDeviceIdCookie, {
		timeout: 10000,
		interval: 1000,
	});
	if (!deviceId) {
		console.log("[Nav] Time elapsed and values were not present for the check");
		return false;
	}
	const loggedIn = !!user?.authToken;
	const payload = { swid, deviceId };
	const cacheKey = buildDaCheckCacheKey(swid, deviceId, loggedIn);
	const headers = { "Content-Type": "application/json" };
	if (user?.authToken) {
		headers.Authorization = user.authToken;
	}
	try {
		// Serve from cache when a fresh entry exists for this user.
		const cached = await getCache(cacheKey);
		if (cached) {
			return evaluateDaResponse(await cached.json());
		}
		// Request the clean endpoint (no query params); payload rides in the body.
		const response = await fetch(resolveDaCheckUrl(), {
			method: "POST",
			headers,
			body: JSON.stringify(payload),
		});
		if (!response.ok) return false;
		// Cache the response under the user-specific synthetic key.
		await setCache(cacheKey, response.clone(), daCheckCacheMinutes);
		// Guard against 204 / empty-body responses, which would throw in json().
		const data = response.status === 204 ? {} : await response.json();
		return evaluateDaResponse(data);
	} catch {
		return false;
	}
};
